// components/style.ts
import styled, { keyframes, css } from 'styled-components';

/* ============ 动画 ============ */
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.4; transform: scale(0.85); }
`;

/* ============ 容器 ============ */
export const Container = styled.div<{ $isOpen?: boolean }>`
  position: relative;
  display: inline-block;
  font-family: inherit;
`;

/* ============ 触发按钮 ============ */
export const TriggerButton = styled.button<{
  $isOpen?: boolean;
  $chainId?: number;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 14px;
  border-radius: 12px;
  border: none;
  background: #ffffff;
  color: #111827;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f9fafb;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const ChainInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const ChainIcon = styled.div<{ $chainId?: number }>`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 14px;
  line-height: 1;

  img {
    width: 100%;
    height: 100%;
  }
`;

export const ChainName = styled.span`
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: #111827;
  @media (max-width: 768px) {
    display: none;
  }
`;

/* ============ Loading ============ */
export const LoadingSpinner = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(17, 24, 39, 0.15);
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  flex-shrink: 0;
`;

/* ============ 下拉面板 ============ */
export const Dropdown = styled.div<{ $offsetX?: number }>`
  position: absolute;
  top: calc(100% + 8px);
  right: ${({ $offsetX = 0 }) => `${$offsetX}px`};
  width: min(320px, calc(100vw - 24px));
  max-height: 420px;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  box-shadow: 0 12px 32px rgba(17, 24, 39, 0.12);
  z-index: 1000;
  animation: ${fadeIn} 0.18s ease-out;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const DropdownHeader = styled.div`
  padding: 16px 18px 12px;
  border-bottom: 1px solid #f3f4f6;
`;

export const DropdownTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #111827;
`;

export const DropdownSubtitle = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 3px;
`;

/* ============ 链列表 ============ */
export const ChainList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

export const ChainItem = styled.button<{
  $isActive?: boolean;
  $chainId?: number;
}>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px;
  border-radius: 10px;
  border: 1px solid transparent;
  background: transparent;
  color: #111827;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:not(:last-child) {
    margin-bottom: 4px;
  }

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }

  &:disabled {
    cursor: default;
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      background: rgba(99, 102, 241, 0.08);
      border-color: rgba(99, 102, 241, 0.25);
    `}
`;

export const ChainItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

export const ChainItemIcon = styled.div<{ $chainId?: number }>`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #f3f4f6;
  font-size: 16px;

  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: contain;
  }
`;

export const ChainItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

export const ChainItemName = styled.span<{ $isActive?: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${({ $isActive }) => ($isActive ? '#6366f1' : '#111827')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ChainItemId = styled.span`
  font-size: 11px;
  color: #9ca3af;
`;

export const ChainItemRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding-left: 4px;
`;

export const ActiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #6366f1;
  background: rgba(99, 102, 241, 0.12);
  border-radius: 999px;
`;

export const StatusDot = styled.span<{ $color?: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color || '#9ca3af'};
  box-shadow: 0 0 6px ${({ $color }) => $color || '#9ca3af'}66;
  margin-right: 2px;
`;

/* ============ 底部 ============ */
export const DropdownFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  border-top: 1px solid #f3f4f6;
  font-size: 11px;
`;

export const FooterText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #6b7280;
`;

export const LiveDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22c55e;
  animation: ${pulse} 1.6s ease-in-out infinite;
`;